import { FC, useEffect, useRef, useState } from 'react';
import MarkdownRenderer from './markdown-renderer/MarkdownRenderer';
import './app-msg.scss';
import ReactMarkdown from 'react-markdown';
import MsgOptions from './MsgOptions';
import StreamMsg from './StreamMsg';
import Suggestions from './Suggestions';
import Tooltip from './common/Tooltip';
import useWindowDimensions from '../hooks/useWindowDimensions';
import TextSelectionPopup from './TextSelectionPopup';

interface ChatMsgDataProps {
  ques: string;
  msgId: string;
  answer: string;
  disableMsgOptions?: boolean;
  isLastMsg?: boolean;
  replyText?: string;
}

const ChatMsg: FC<ChatMsgDataProps> = ({
  ques,
  answer,
  msgId,
  disableMsgOptions,
  isLastMsg,
  replyText,
}) => {
  const [copied, setCopied] = useState(false);
  const { width } = useWindowDimensions();
  const [selectedText, setSelectedText] = useState('');
  const [popupPosition, setPopupPosition] = useState({ x: 0, y: 0 });
  const [showPopup, setShowPopup] = useState(false);
  const messageContainerRef = useRef<HTMLDivElement>(null);
  const copyTextToClipboard = () => {
    setCopied(true);
    setTimeout(() => {
      setCopied(false);
    }, 2000);
    if (navigator && 'clipboard' in navigator) {
      return navigator.clipboard.writeText(ques);
    } else {
      return document.execCommand('copy', true, ques);
    }
  };

  const handleTextSelection = () => {
    const selection = window.getSelection();
    const text = selection?.toString().trim();

    if (text && text.length > 0) {
      const range = selection?.getRangeAt(0);
      const rect = range?.getBoundingClientRect();

      if (rect) {
        setSelectedText(text);
        const popupHeight = 100;
        const spaceBelow = window.innerHeight - rect.bottom;
        const spaceAbove = rect.top;
        const showAbove = spaceBelow < popupHeight && spaceAbove > spaceBelow;

        setPopupPosition({
          x: rect.left + rect.width / 2 - 80 + window.scrollX,
          y: showAbove
            ? rect.top + window.scrollY - popupHeight
            : rect.bottom + window.scrollY + 8,
        });
        setShowPopup(true);
      }
    } else {
      setShowPopup(false);
    }
  };

  useEffect(() => {
    const container = messageContainerRef.current;
    if (container) {
      container.addEventListener('mouseup', handleTextSelection);
      return () => {
        container.removeEventListener('mouseup', handleTextSelection);
      };
    }
  }, []);

  if (!ques) {
    return <></>;
  }

  return (
    <div ref={messageContainerRef} className={`flex app-chat-msg flex-col dark:text-white relative`}>
      {replyText && (
        <div className="flex items-start cursor-pointer opacity-60 mb-3 hover:opacity-100 transition-all gap-3 w-full justify-center">
          <i className="icon-enter text-[18px]"></i>
          <div className="text-[14px] text-center">{replyText}</div>
        </div>
      )}
      <div className="flex items-start">
        <div className="grow sender">
          <div className="flex justify-end group relative">
            {ques && (
              <>
                <div
                  className={`
                  font-normal whitespace-pre-wrap break-words bg-[rgba(233,233,233,.5)] px-3 py-2
                  rounded-2xl user-ques max-w-[100%] sm:max-w-[80%] lg:max-w-[70%] dark:bg-[#434343] mb-8`}
                >
                  <ReactMarkdown>{ques}</ReactMarkdown>
                </div>
                <Tooltip
                  content={copied || width < 500 ? 'Copied' : 'Copy'}
                  direction="top"
                >
                  <i
                    className="
                      icon-copy text-xl mr-3 cursor-pointer text-appText hover:text-black
                      dark:hover:text-white opacity-0 group-hover:opacity-100 dark:text-white
                      absolute bottom-1 right-0
                      "
                    onClick={() => copyTextToClipboard()}
                  ></i>
                </Tooltip>
              </>
            )}
          </div>
          {answer && <MarkdownRenderer markdownText={answer} />}
          {isLastMsg && <StreamMsg />}
        </div>
      </div>

      {answer && (
        <div className="h-[40px]">
          <MsgOptions
            message={answer}
            msgId={msgId}
            // isGoodResponse={isGoodResponse}
            isLastMsg={isLastMsg}
            // isImage={isImage}
            // isCompareModel={isCompare}
          />
        </div>
      )}

      {isLastMsg && <Suggestions answerLoaded={answer.length > 0} />}

      {showPopup && answer && (
        <TextSelectionPopup
          selectedText={selectedText}
          position={popupPosition}
          onClose={() => setShowPopup(false)}
        />
      )}
    </div>
  );
};

export default ChatMsg;
